import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories, heroSlides, testimonials } from '@/mocks/comics';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToSlide = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  useEffect(() => {
    const timer = setInterval(() => {
      goToSlide((currentSlide + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [currentSlide, goToSlide, heroSlides.length]);

  const hotComics = comics.filter((c) => c.rating >= 4.7).slice(0, 6);
  const latestComics = [...comics].sort((a, b) => b.updateTime.localeCompare(a.updateTime)).slice(0, 8);
  const completedComics = comics.filter((c) => c.status === '已完结').slice(0, 4);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩漫星球',
    url: 'https://hanmanxingqiu.com',
    description: '韩漫免费,漫画下拉式阅读,好看的韩国漫画大全,韩漫网站。韩漫网站提供免费漫画下拉式阅读和好看的韩国漫画大全，资源多样操作简单，无需付费即可探索各种故事，让您的休闲时间更充实。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://hanmanxingqiu.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SEO
        title="韩漫免费_漫画下拉式阅读_好看的韩国漫画大全_韩漫星球"
        description="韩漫星球提供免费漫画下拉式阅读和好看的韩国漫画大全，资源多样操作简单，无需付费即可探索各种故事，让您的休闲时间更充实。"
        keywords="韩漫免费,漫画下拉式阅读,好看的韩国漫画大全,韩漫网站"
        canonicalUrl="https://hanmanxingqiu.com"
        ogImage="https://readdy.ai/api/search-image?query=Korean%20manhwa%20website%20preview%20with%20colorful%20comic%20covers%20collage%2C%20vibrant%20and%20eye-catching%2C%20warm%20tones&width=1200&height=630&seq=og-home&orientation=landscape"
        jsonLd={jsonLd}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative h-[500px] md:h-[620px] overflow-hidden">
          {heroSlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
            >
              <img src={slide.bg} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
            </div>
          ))}

          <div className="absolute inset-0 z-20 flex items-center">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-3xl">
                <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight transition-all duration-500" style={{ fontFamily: 'var(--font-heading)' }}>
                  {heroSlides[currentSlide].title}
                </h1>
                <p className="text-white/80 text-base md:text-lg mb-8 max-w-xl">
                  {heroSlides[currentSlide].subtitle}
                </p>
                <Link
                  to={heroSlides[currentSlide].link}
                  className="inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white px-6 py-3 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap"
                >
                  {heroSlides[currentSlide].cta}
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${index === currentSlide ? 'bg-rose-500 w-8' : 'bg-white/50 hover:bg-white/80'}`}
                aria-label={`第${index + 1}张`}
              />
            ))}
          </div>
        </section>

        {/* Categories */}
        <section className="py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                漫画分类
              </h2>
              <Link to="/category/love" className="text-sm text-foreground-500 hover:text-rose-500 transition-colors whitespace-nowrap flex items-center gap-1">
                查看全部 <i className="ri-arrow-right-line text-xs" />
              </Link>
            </div>
            <div className="grid grid-cols-5 md:grid-cols-10 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-2 group"
                >
                  <div className={`w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${cat.color}`}>
                    <i className={`${cat.icon} text-xl md:text-2xl`} />
                  </div>
                  <span className="text-xs md:text-sm text-foreground-700 font-medium whitespace-nowrap">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Comics */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                热门推荐
              </h2>
              <span className="flex items-center gap-1 text-sm text-foreground-400">
                <i className="ri-fire-line text-rose-500" /> 高分精选
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {hotComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                最近更新
              </h2>
              <span className="flex items-center gap-1 text-sm text-foreground-400">
                <i className="ri-time-line text-rose-500" /> 每日更新
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {latestComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Completed Comics */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                已完结佳作
              </h2>
              <span className="flex items-center gap-1 text-sm text-foreground-400">
                <i className="ri-check-double-line text-emerald-500" /> 一次看个够
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {completedComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-14 md:py-20">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-foreground-950 mb-12" style={{ fontFamily: 'var(--font-heading)' }}>
              为什么选择韩漫星球
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-book-open-line', title: '海量资源', desc: '收录数千部优质韩国漫画，涵盖各类题材，满足不同读者的阅读需求。' },
                { icon: 'ri-download-cloud-line', title: '完全免费', desc: '所有漫画资源全部免费阅读，无需付费订阅，让每个人都能享受阅读的乐趣。' },
                { icon: 'ri-smartphone-line', title: '下拉式阅读', desc: '专为移动端优化的下拉式漫画阅读体验，流畅顺滑，操作简单方便。' },
                { icon: 'ri-refresh-line', title: '每日更新', desc: '热门漫画每日同步更新，追更不等待，第一时间获取最新章节内容。' },
              ].map((item) => (
                <div key={item.title} className="text-center p-6">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-rose-100 flex items-center justify-center">
                    <i className={`${item.icon} text-2xl text-rose-500`} />
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-center text-foreground-950 mb-10" style={{ fontFamily: 'var(--font-heading)' }}>
              用户好评
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-white rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                      <div className="flex gap-0.5 mt-0.5">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-yellow-400 text-xs" />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-center text-foreground-950 mb-10" style={{ fontFamily: 'var(--font-heading)' }}>
              常见问题
            </h2>
            <div className="space-y-3">
              {[
                { q: '韩漫星球的漫画真的全部免费吗？', a: '是的，韩漫星球上所有漫画资源完全免费，无需任何付费订阅或会员。我们致力于为漫画爱好者提供无障碍的阅读体验。' },
                { q: '支持哪些设备阅读？', a: '韩漫星球支持电脑、平板和手机等多种设备访问。我们采用响应式设计，无论您使用什么设备，都能获得流畅的下拉式阅读体验。' },
                { q: '漫画更新频率如何？', a: '热门连载漫画每日同步更新，确保您能第一时间追更。部分漫画根据原作更新节奏，每周更新2-3次。' },
                { q: '如何找到我想看的漫画？', a: '您可以通过首页的分类导航浏览不同题材的漫画，也可以使用顶部的搜索框直接搜索您想看的漫画名称或作者。' },
                { q: '阅读记录会被保存吗？', a: '目前韩漫星球支持浏览器本地记录您的阅读进度。我们正在开发账号系统，届时将支持跨设备的阅读记录同步功能。' },
              ].map((faq, i) => (
                <details key={i} className="group bg-white rounded-xl border border-background-200">
                  <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 list-none">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20illustration%20collage%20with%20various%20comic%20panels%20and%20characters%2C%20warm%20artistic%20watercolor%20style%2C%20soft%20gradient%20from%20coral%20to%20cream%2C%20dreamy%20atmosphere%2C%20wide%20cinematic&width=1600&height=500&seq=cta-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-rose-500/90 to-amber-500/80" />
          <div className="relative z-10 text-center px-4">
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              准备好开启您的漫画之旅了吗？
            </h2>
            <p className="text-white/80 text-base mb-8 max-w-lg mx-auto">
              数千部韩国漫画等您来探索，完全免费，立即开始阅读！
            </p>
            <Link
              to="/category/love"
              className="inline-flex items-center gap-2 bg-white text-rose-500 px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-white/90 transition-all duration-300 whitespace-nowrap"
            >
              立即浏览 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}